import React, { useState } from 'react';
import { X, Edit2, Download, Check, Copy, Layers, Mail } from 'lucide-react';
import InvioEmailModal from '../InvioEmailModal';

const statoLabels = { bozza: 'Bozza', approvato: 'Approvato', cantiere_aperto: 'Cantiere Aperto', validato: 'Validato Listino' };
const statoColors = { bozza: 'bg-slate-100 text-slate-700', approvato: 'bg-blue-50 text-blue-700', cantiere_aperto: 'bg-green-50 text-green-700', validato: 'bg-purple-50 text-purple-700' };
const fmt = (n) => `${(Number(n) || 0).toFixed(2)} €`;

/**
 * Modale di dettaglio (read-only) di un preventivo, con vista a sezioni o flat.
 */
export default function PreventivoDetailModal({
  preventivo,
  actionLoading,
  onClose,
  onEdit,
  onDownloadPdf,
  onApprova,
  onValida,
  onDuplica,
}) {
  if (!preventivo) return null;
  const hasSezioni = Array.isArray(preventivo.sezioni) && preventivo.sezioni.length > 0;
  const [showInvioEmail, setShowInvioEmail] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 modal-overlay sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-6xl max-h-[92vh] sm:max-h-[90vh] flex flex-col animate-fade-in modal-content overflow-hidden">
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-200 bg-white sticky top-0 z-10">
          <div className="min-w-0 flex-1">
            <h3 className="text-xl font-bold truncate">{preventivo.titolo || preventivo.numero_preventivo}</h3>
            {preventivo.oggetto && <p className="text-sm italic text-slate-600 truncate">{preventivo.oggetto}</p>}
            <p className="text-sm text-slate-500">
              {preventivo.numero_preventivo} - <span className={`px-2 py-0.5 rounded text-xs font-bold ${statoColors[preventivo.stato]}`}>{statoLabels[preventivo.stato] || preventivo.stato}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
            {preventivo.stato === 'bozza' && (
              <button onClick={() => onEdit(preventivo)} className="px-3 py-1.5 border border-slate-200 text-xs font-bold rounded-md hover:bg-slate-50 flex items-center gap-1">
                <Edit2 size={14}/>Modifica
              </button>
            )}
            <button onClick={() => onDownloadPdf(preventivo.id, preventivo.numero_preventivo)} disabled={actionLoading.startsWith('pdf')} data-testid="download-pdf-btn"
              className="px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-md hover:bg-primary-hover transition-all flex items-center gap-1 disabled:opacity-50">
              <Download size={14} /> {actionLoading.startsWith('pdf') ? 'Scaricamento...' : 'Scarica PDF'}
            </button>
            <button onClick={() => setShowInvioEmail(true)} data-testid="invia-preventivo-email-btn"
              className="px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-md hover:bg-primary transition-all flex items-center gap-1">
              <Mail size={14} /> Invia per email
            </button>
            <button onClick={onClose} aria-label="Chiudi"><X size={20} className="text-slate-400" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          {/* Actions bar */}
          <div className="flex gap-2 mb-4 p-3 bg-slate-50 rounded-md flex-wrap">
            {preventivo.stato === 'bozza' && (
              <button onClick={() => onApprova(preventivo.id)} disabled={actionLoading.startsWith('approva')} data-testid="approva-detail-btn"
                className="px-4 py-2 bg-accent-success text-white text-sm font-bold rounded-md hover:bg-green-700 flex items-center gap-1 disabled:opacity-50">
                <Check size={16}/>{actionLoading.startsWith('approva') ? 'Approvazione...' : 'Approva e Apri Cantiere'}
              </button>
            )}
            {(preventivo.stato === 'bozza' || preventivo.stato === 'approvato') && (
              <button onClick={() => onValida(preventivo.id)} disabled={actionLoading.startsWith('valida')} data-testid="valida-detail-btn"
                className="px-4 py-2 bg-purple-600 text-white text-sm font-bold rounded-md hover:bg-purple-700 flex items-center gap-1 disabled:opacity-50">
                <Check size={16}/>{actionLoading.startsWith('valida') ? 'Validazione...' : 'Valida Prezzi Listino'}
              </button>
            )}
            <button onClick={() => onDuplica(preventivo)} className="px-4 py-2 border border-slate-200 text-sm font-bold rounded-md hover:bg-slate-50 flex items-center gap-1">
              <Copy size={16}/>Crea Nuova Versione
            </button>
          </div>

          {hasSezioni ? (
            <div className="space-y-4 mb-4">
              {preventivo.sezioni.map((sez, sIdx) => (
                <div key={sIdx} className="border border-slate-200 rounded-md overflow-hidden">
                  <div className="px-3 py-2 bg-purple-50 border-b border-purple-100">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <h4 className="font-bold text-sm flex items-center gap-2">
                          <Layers size={14} className="text-purple-600" /> § {sIdx + 1} - {sez.nome}
                        </h4>
                        {sez.oggetto && <p className="text-xs italic text-slate-600 mt-0.5">{sez.oggetto}</p>}
                      </div>
                      <span className="text-xs font-bold px-2 py-1 bg-primary text-primary rounded">IVA {sez.iva ?? 22}%</span>
                    </div>
                    {sez.note && <p className="text-xs text-slate-500 mt-1">📝 {sez.note}</p>}
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-3 py-1.5 text-[10px] uppercase font-bold text-slate-500">Descrizione</th>
                        <th className="text-right px-3 py-1.5 text-[10px] uppercase font-bold text-slate-500">Qtà</th>
                        <th className="text-left px-3 py-1.5 text-[10px] uppercase font-bold text-slate-500">U.M.</th>
                        <th className="text-right px-3 py-1.5 text-[10px] uppercase font-bold text-slate-500">Prezzo</th>
                        <th className="text-right px-3 py-1.5 text-[10px] uppercase font-bold text-slate-500">Totale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sez.righe || []).map((r, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="px-3 py-1.5 text-xs">{r.descrizione}</td>
                          <td className="px-3 py-1.5 text-right text-xs">{r.quantita}</td>
                          <td className="px-3 py-1.5 text-xs">{r.unita_misura}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-xs">{fmt(r.prezzo_unitario)}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-xs font-bold">{fmt(r.totale_riga)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="px-3 py-2 bg-primary/50 border-t border-primary text-right text-xs">
                    <span className="text-slate-500 mr-2">Subtotale: <span className="font-mono">{fmt(sez.subtotale_imponibile)}</span></span>
                    <span className="text-slate-500 mr-2">+IVA: <span className="font-mono">{fmt(sez.subtotale_iva)}</span></span>
                    <span className="font-bold">Tot: <span className="font-mono text-primary">{fmt(sez.subtotale_finale)}</span></span>
                  </div>
                </div>
              ))}
              <div className="bg-primary border border-primary rounded-md overflow-hidden">
                <div className="px-3 py-2 bg-primary text-white text-xs font-bold">RIEPILOGO SEZIONI</div>
                <table className="w-full text-sm">
                  <thead className="bg-primary/60">
                    <tr>
                      <th className="text-left px-3 py-1.5 text-[10px] uppercase font-bold">Sezione</th>
                      <th className="text-right px-3 py-1.5 text-[10px] uppercase font-bold">Imponibile</th>
                      <th className="text-right px-3 py-1.5 text-[10px] uppercase font-bold">IVA</th>
                      <th className="text-right px-3 py-1.5 text-[10px] uppercase font-bold">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preventivo.sezioni.map((sez, i) => (
                      <tr key={i} className="border-b border-primary">
                        <td className="px-3 py-1.5 text-xs font-semibold">{sez.nome}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs">{fmt(sez.subtotale_imponibile)}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs">{sez.iva}% — {fmt(sez.subtotale_iva)}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs font-bold">{fmt(sez.subtotale_finale)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <table className="w-full text-sm mb-4">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 text-xs uppercase font-bold text-slate-500">Descrizione</th>
                  <th className="text-left px-3 py-2 text-xs uppercase font-bold text-slate-500">Codice</th>
                  <th className="text-right px-3 py-2 text-xs uppercase font-bold text-slate-500">Qta</th>
                  <th className="text-left px-3 py-2 text-xs uppercase font-bold text-slate-500">U.M.</th>
                  <th className="text-right px-3 py-2 text-xs uppercase font-bold text-slate-500">Prezzo</th>
                  <th className="text-right px-3 py-2 text-xs uppercase font-bold text-slate-500">Totale</th>
                </tr>
              </thead>
              <tbody>
                {preventivo.righe?.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-3 py-2">{r.descrizione}</td>
                    <td className="px-3 py-2 font-mono text-xs text-primary">{r.codice_prezziario}</td>
                    <td className="px-3 py-2 text-right">{r.quantita}</td>
                    <td className="px-3 py-2 text-xs">{r.unita_misura}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(r.prezzo_unitario)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{fmt(r.totale_riga)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="border-t border-slate-200 pt-3 text-right space-y-1">
            <p className="text-sm">Imponibile: <span className="font-mono font-bold">{fmt(preventivo.totale_imponibile)}</span></p>
            <p className="text-sm">IVA {hasSezioni ? 'mista' : `${preventivo.iva_default ?? 22}%`}: <span className="font-mono">{fmt(preventivo.totale_iva)}</span></p>
            <p className="text-lg font-black">Totale: <span className="font-mono">{fmt(preventivo.totale_finale)}</span></p>
          </div>
          {preventivo.note_finali && (
            <div className="mt-4 p-3 bg-slate-50 rounded-md">
              <p className="text-xs font-bold text-slate-500 mb-1">NOTE</p>
              <p className="text-sm">{preventivo.note_finali}</p>
            </div>
          )}
          {preventivo.esclusioni && (
            <div className="mt-2 p-3 bg-red-50 rounded-md">
              <p className="text-xs font-bold text-red-500 mb-1">ESCLUSIONI</p>
              <p className="text-sm">{preventivo.esclusioni}</p>
            </div>
          )}
        </div>
      </div>
      <InvioEmailModal
        open={showInvioEmail}
        onClose={() => setShowInvioEmail(false)}
        entity="preventivi"
        docId={preventivo.id}
        docLabel="Preventivo"
        defaultOggetto={`Preventivo EDIL CIMA - ${preventivo.codice_preventivo || preventivo.titolo || ''}`}
      />
    </div>
  );
}
